import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { buildQueryString } from '@/lib/api/client';
import { recipientsFetch } from '@/lib/recipients/api';
import { toast } from 'sonner';
import { customFieldKeys } from '@/hooks/api/query-keys';
import type { CustomField } from '@/lib/types/recipients';
import type { PaginatedResult } from '@/hooks/api/pagination';
import { runPerId } from '@/lib/recipients/run-per-id';
import { ApiClientError } from '@/lib/api/client';
import { CUSTOM_FIELD_REPORTING_ERROR_KEYS } from '@/lib/population/error-keys';
import { populationKeys } from '@/lib/population/query-keys';
import { useTranslation } from '@/lib/i18n';
import { presetLabelKey } from '@/lib/recipients/preset-option-labels';
import { toastRecipientRefusal } from '@/lib/recipients/refusal-codes';

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ============ Query Hooks ============

/** Every custom field of the workspace as a flat array. Used in field mapping
 *  dropdowns, the recipients page, the profile, the rule builder, the "key
 *  already exists" check and the next free field_<n> key.
 *
 *  `GET /recipients/custom-fields` is server-paginated (default limit 25,
 *  max 100) while a workspace may define up to 50 fields. This hook used to
 *  take that single default page, so fields 26+ were missing from the list
 *  page, the profile, import mapping and the rule builder (audit 2026-09-25
 *  F5), and a new Arabic or Hindi field was offered a taken key (409; locale
 *  audit 2026-09-26, INPUT-12). Pages through every result at the endpoint's
 *  max page size. */
export function useCustomFields() {
  return useQuery({
    queryKey: customFieldKeys.list(),
    queryFn: fetchAllCustomFields,
  });
}

/**
 * Every custom field of the workspace — every page of
 * `GET /recipients/custom-fields` at its max page size. THE way to read the
 * list: useCustomFields uses it, and so does a screen that must read the list
 * inside its own (e.g. workspace-scoped) query. A bare `custom-fields` GET
 * returns the first 25 only (audit 2026-09-25 F5 / wave-3 item 11);
 * lib/__tests__/custom-field-list-reads-through-the-hook.guard.spec.ts fails
 * on a list read anywhere else.
 */
export async function fetchAllCustomFields(): Promise<CustomField[]> {
  const LIMIT = 100; // /recipients/custom-fields max page size
  const all: CustomField[] = [];
  let page = 1;
  for (let i = 0; i < 100; i++) {
    const qs = buildQueryString({ page, limit: LIMIT });
    const result = await recipientsFetch<
      { data: CustomField[]; total?: number } | CustomField[]
    >(`custom-fields${qs}`);
    // A bare array carries no paging envelope — it is the whole set.
    if (Array.isArray(result)) {
      all.push(...result);
      break;
    }
    const batch = result?.data ?? [];
    all.push(...batch);
    if (batch.length < LIMIT) break;
    if (typeof result.total === 'number' && all.length >= result.total) break;
    page += 1;
  }
  return all;
}

/** Paginated — returns { data, total, page, limit, totalPages }. Used by the custom fields table. */
export function useCustomFieldsPaginated(filter?: { page?: number; limit?: number }) {
  const defaultLimit = filter?.limit ?? 25;
  return useQuery({
    queryKey: [...customFieldKeys.lists(), 'paginated', filter],
    queryFn: async () => {
      const qs = filter ? buildQueryString(filter as Record<string, unknown>) : '';
      return recipientsFetch<PaginatedResponse<CustomField> | CustomField[]>(`custom-fields${qs}`);
    },
    select: (raw): PaginatedResult<CustomField> => {
      if (Array.isArray(raw)) {
        return { data: raw, total: raw.length, page: 1, limit: raw.length || defaultLimit, totalPages: 1 };
      }
      const r = raw as PaginatedResponse<CustomField>;
      const limit = r.limit ?? defaultLimit;
      const total = r.total ?? 0;
      return {
        data: r.data ?? [],
        total,
        page: r.page ?? 1,
        limit,
        totalPages: r.totalPages ?? Math.max(1, Math.ceil(total / limit)),
      };
    },
  });
}

export function useCustomFieldMetrics() {
  return useQuery({
    queryKey: customFieldKeys.metrics(),
    queryFn: () =>
      recipientsFetch<{
        totalFields: number;
        activeFields: number;
        archivedFields: number;
        averageUsage: number;
      }>('custom-fields/metrics'),
  });
}

// ============ Mutation Hooks ============

/**
 * After a refused write, read the fields again: a refusal often means the list
 * on screen is out of date (the field was deleted in another tab, or gained
 * recipients since the list was read), and the app keeps a list for five
 * minutes. Called by each hook, so it runs even when the page has gone or a
 * second click replaced the first one's callbacks. Not returned: TanStack
 * would wait for it before the page puts its values back, and that undo would
 * then land on top of the fresh list.
 */
function rereadFieldsAfterRefusal(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: customFieldKeys.all() });
}

/**
 * #801: the name of the preset a refusal says took the key, as the Preset
 * library names it (a Global preset translated), or null for any other refusal.
 */
export function inheritedKeyRefusalName(
  err: unknown,
  t: (key: string, params?: Record<string, string | number>) => string,
): string | null {
  if (!(err instanceof ApiClientError) || err.errorCode !== 'custom_field_key_inherited') return null;
  const body = err.body as
    | { inheritedLabel?: unknown; inheritedScope?: unknown; key?: unknown }
    | undefined;
  const key = typeof body?.key === 'string' ? body.key : '';
  const labelKey =
    body?.inheritedScope === 'Global' ? presetLabelKey({ scope: 'Global', key }) : null;
  if (labelKey) return t(labelKey);
  return typeof body?.inheritedLabel === 'string' ? body.inheritedLabel : key;
}

/**
 * `inheritedKeySaidInDialog`: a wizard's Create Field dialog waits for the save
 * and says that refusal itself, under Field Key; a toast would say it twice.
 */
export function useCreateCustomField(options: { inheritedKeySaidInDialog?: boolean } = {}) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (data: Partial<CustomField>) =>
      recipientsFetch<CustomField>('custom-fields', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customFieldKeys.all() });
    },
    onError: (err: Error) => {
      rereadFieldsAfterRefusal(queryClient);
      // #801: a key the workspace inherits from the Preset library, in the
      // admin's language.
      const inherited = inheritedKeyRefusalName(err, t);
      if (inherited !== null) {
        if (!options.inheritedKeySaidInDialog) {
          toast.error(t('recipients.customFieldDialogs.keyInherited', { name: inherited }));
        }
        return;
      }
      toastRecipientRefusal(t, 'Creating a custom field', 'toast.failedCreateField', err);
    },
  });
}

// The edit, delete, deactivate and activate hooks below say a refusal ONCE, in
// the admin's language (toastRecipientRefusal, as use-recipients.ts does):
// what failed, and why, with the API's words in the console. The Fields page,
// their only screen, puts its values back and adds no toast of its own.

export function useUpdateCustomField() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CustomField> }) =>
      recipientsFetch<CustomField>(`custom-fields/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: customFieldKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: customFieldKeys.lists() });
      // "Show in reports" changes the report dimensions (lane B2).
      queryClient.invalidateQueries({ queryKey: populationKeys.dimensions.all() });
    },
    onError: (err: Error) => {
      rereadFieldsAfterRefusal(queryClient);
      // A lane B2 refusal is shown translated by the page (useCustomFieldsPage);
      // the API's English must not appear beside it.
      if (err instanceof ApiClientError && err.errorCode && CUSTOM_FIELD_REPORTING_ERROR_KEYS.has(err.errorCode)) return;
      toastRecipientRefusal(t, 'Updating a custom field', 'toast.failedUpdateField', err);
    },
  });
}

export function useDeleteCustomField() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    // F14 (audit 2026-09-25) — the API refuses to delete a field in use unless
    // `force=true`; the delete dialog asks the operator to confirm that loss
    // explicitly and then passes `force`.
    mutationFn: (target: string | { id: string; force?: boolean }) => {
      const { id, force } = typeof target === 'string' ? { id: target, force: false } : target;
      return recipientsFetch<void>(`custom-fields/${id}${force ? '?force=true' : ''}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customFieldKeys.all() });
    },
    onError: (err: Error) => {
      rereadFieldsAfterRefusal(queryClient);
      toastRecipientRefusal(t, 'Deleting a custom field', 'toast.failedDeleteField', err);
    },
  });
}

/** What a bulk action on the Fields page does to each selected field. */
export type CustomFieldBulkAction = 'activate' | 'deactivate' | 'delete';

/**
 * Wave-3 item 9 (audit 2026-09-25) — Activate / Deactivate / Delete on many
 * fields. The page used to call `mutate(id)` once per field on the single
 * hooks: each call revalidated the list on its own, a toast claimed success
 * before any answer, and TanStack reports mutate()-level callbacks only for
 * the last call. The API has no bulk route for fields, so this sends the
 * per-id requests as ONE batch (runPerId), revalidates ONCE when all have
 * answered, and returns what happened to each id for the page to report.
 * Delete never forces: a field in use is refused and reported as kept.
 */
export function useBulkCustomFieldAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ action, ids }: { action: CustomFieldBulkAction; ids: string[] }) =>
      runPerId(ids, (id) =>
        action === 'delete'
          ? recipientsFetch<void>(`custom-fields/${id}`, { method: 'DELETE' })
          : recipientsFetch<CustomField>(
              `custom-fields/${id}/${action === 'activate' ? 'restore' : 'archive'}`,
              { method: 'POST' },
            ),
      ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: customFieldKeys.all() });
    },
  });
}

export function useArchiveCustomField() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id: string) =>
      recipientsFetch<CustomField>(`custom-fields/${id}/archive`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customFieldKeys.all() });
    },
    onError: (err: Error) => {
      rereadFieldsAfterRefusal(queryClient);
      toastRecipientRefusal(t, 'Deactivating a custom field', 'toast.failedArchiveField', err);
    },
  });
}

export function useRestoreCustomField() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id: string) =>
      recipientsFetch<CustomField>(`custom-fields/${id}/restore`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customFieldKeys.all() });
    },
    onError: (err: Error) => {
      rereadFieldsAfterRefusal(queryClient);
      toastRecipientRefusal(t, 'Activating a custom field', 'toast.failedToggleFieldStatus', err);
    },
  });
}

export function useReorderCustomFields() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      recipientsFetch<void>('custom-fields/reorder', {
        method: 'POST',
        body: JSON.stringify({
          items: ids.map((id, index) => ({ id, sortOrder: index + 1 })),
        }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customFieldKeys.lists() });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}
